import { CaretDown, Function, Table } from "@phosphor-icons/react"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"

const spreadsheetData: Record<string, Record<number, string>> = {
  A: {
    1: "Quarters",
    2: "Income Statement",
    3: "Fees",
    4: "Fees_Supply_Side",
    5: "Revenue",
    6: "Expenses",
    7: "Token_Incentives",
    8: "Earnings",
    9: "Operating_Expenses",
    10: "Research_Development",
    11: "Sales_Marketing",
    12: "General_Administrative",
    13: "Operating_Income",
    14: "Other_Income",
    15: "Interest_Expense",
    16: "Net_Income",
  },
  B: {
    1: "Q1 2026",
    3: "$4,716,702.18",
    4: "$4,010,677.26",
    5: "$706,024.92",
    6: "$97,171,161.57",
    7: "$97,171,161.57",
    8: "($96,465,136.64)",
    9: "$12,450,000.00",
    10: "$8,200,000.00",
    11: "$2,850,000.00",
    12: "$1,400,000.00",
    13: "($11,743,975.08)",
    14: "$125,000.00",
    15: "$0.00",
    16: "($96,340,136.64)",
  },
  C: {
    1: "Q4 2025",
    3: "$76,566,498.54",
    4: "$48,093,226.99",
    5: "$28,473,271.56",
    6: "$765,529,137.32",
    7: "$765,529,137.32",
    8: "($737,055,865.7)",
    9: "$45,200,000.00",
    10: "$28,500,000.00",
    11: "$10,200,000.00",
    12: "$6,500,000.00",
    13: "($16,726,728.44)",
    14: "$850,000.00",
    15: "$0.00",
    16: "($736,205,865.7)",
  },
  D: {
    1: "Q3 2025",
    3: "$125,341,114.86",
    4: "$69,046,943.74",
    5: "$56,294,171.12",
    6: "$933,698,411.71",
    7: "$933,698,411.71",
    8: "($877,404,240.5)",
    9: "$52,800,000.00",
    10: "$32,000,000.00",
    11: "$12,500,000.00",
    12: "$8,300,000.00",
    13: "$3,494,171.12",
    14: "$1,200,000.00",
    15: "$0.00",
    16: "($876,204,240.5)",
  },
  E: {
    1: "Q2 2025",
    3: "$103,303,663.73",
    4: "$52,989,953.17",
    5: "$50,313,710.56",
    6: "$528,485,214.55",
    7: "$528,485,214.55",
    8: "($478,171,503.9)",
    9: "$38,500,000.00",
    10: "$22,000,000.00",
    11: "$9,800,000.00",
    12: "$6,700,000.00",
    13: "$11,813,710.56",
    14: "$650,000.00",
    15: "$0.00",
    16: "($477,521,503.9)",
  },
}

const columns = ["A", "B", "C", "D", "E"]
const rows = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]

function getCellContent(col: string, row: number): string {
  return spreadsheetData[col]?.[row] || ""
}

function isNumericCell(col: string, row: number): boolean {
  if (col === "A") return false
  if (row === 1) return false
  return !!getCellContent(col, row)
}

export function MasonrySheetsWidget() {
  return (
    <MasonryWidget
      href={PRODUCTS.SHEETS_ROUTE}
      icon={Table}
      productName="Sheets"
      widgetName="Sheets integration"
      description="Pull blockchain data directly into Excel and Google Sheets with custom functions for financial statements, metrics, and more."
      height={500}
      featuredVisual={
        <div
          className="relative border-t border-l border-solid border-border-default rounded-tl-xl flex-1 min-h-0"
          style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
        >
          <div
            className="bg-bg-default"
            style={{ width: "calc(100% + 100px)" }}
          >
            <div className="flex items-center gap-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
              <div className="flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800">
                <input
                  type="text"
                  value="A1"
                  readOnly
                  className="w-10 text-xs font-medium text-fg-default bg-transparent border-none outline-hidden cursor-pointer"
                />
                <button className="text-fg-secondary hover:text-fg-default">
                  <CaretDown className="w-3 h-3" />
                </button>
              </div>

              <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-neutral-950">
                <Function className="w-4 h-4 text-fg-secondary" />
                <div className="flex-1 font-mono text-xs">
                  <span className="text-fg-default">
                    =TT_FINANCIAL_STATEMENT(
                  </span>
                  <span className="text-green-600 dark:text-green-400">
                    &quot;ETH&quot;
                  </span>
                  <span className="text-fg-default">, </span>
                  <span className="text-green-600 dark:text-green-400">
                    &quot;quarter&quot;
                  </span>
                  <span className="text-fg-default">)</span>
                </div>
              </div>
            </div>

            <div className="flex">
              <div className="w-8 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                <div className="border-b border-neutral-200 dark:border-neutral-800">
                  <div style={{ height: "24px" }} />
                </div>
                {rows.map((rowNum) => (
                  <div
                    key={rowNum}
                    className="border-b border-neutral-200 dark:border-neutral-800"
                  >
                    <div
                      className={`flex items-center justify-center text-xs font-medium ${
                        rowNum === 1
                          ? "bg-neutral-200 dark:bg-neutral-800"
                          : "bg-neutral-50 dark:bg-neutral-900"
                      }`}
                      style={{ height: "24px" }}
                    >
                      {rowNum}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex-1">
                <div className="flex border-b border-neutral-200 dark:border-neutral-800">
                  {columns.map((col) => (
                    <div
                      key={col}
                      className={`w-24 shrink-0 flex items-center justify-center text-xs border-r border-neutral-200 dark:border-neutral-800 ${
                        col === "A"
                          ? "bg-blue-100 dark:bg-blue-900/30"
                          : "bg-white dark:bg-neutral-950"
                      }`}
                      style={{ height: "24px" }}
                    >
                      {col}
                    </div>
                  ))}
                </div>

                <div className="flex flex-col">
                  {rows.map((rowNum) => (
                    <div
                      key={rowNum}
                      className="flex border-b border-neutral-200 dark:border-neutral-800"
                    >
                      {columns.map((col) => {
                        const isA1 = col === "A" && rowNum === 1
                        const cellContent = getCellContent(col, rowNum)
                        const isNumeric = isNumericCell(col, rowNum)

                        return (
                          <div
                            key={`${col}${rowNum}`}
                            className={`w-24 shrink-0 flex items-center px-1.5 text-xs border-r border-neutral-200 dark:border-neutral-800 relative ${
                              isA1
                                ? "bg-blue-50 dark:bg-blue-950/20"
                                : "bg-white dark:bg-neutral-950"
                            } ${isNumeric ? "justify-end" : "justify-start"}`}
                            style={{ height: "24px" }}
                          >
                            {isA1 && (
                              <>
                                <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none z-10" />
                                <div className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 cursor-nwse-resize z-10" />
                              </>
                            )}
                            {cellContent && (
                              <span className="text-xs relative z-0 text-fg-default truncate">
                                {cellContent}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  )
}
